// ============================================================
// COMPANY / TENANT TYPES – Sprint P1-P6
// ============================================================

export type SubscriptionPlanCode = 'free' | 'basic' | 'pro' | 'enterprise';
export type SubscriptionStatus = 'active' | 'expired' | 'cancelled' | 'trial';
export type BillingCycle = 'monthly' | 'yearly';

export interface Company {
  id: string;
  code: string;
  name: string;
  description?: string | null;
  logo_path?: string | null;
  website?: string | null;
  phone?: string | null;
  email?: string | null;
  address?: string | null;
  city?: string | null;
  country: string;
  subscription_plan_code: SubscriptionPlanCode;
  is_active: boolean;
  is_locked: boolean;
  locked_reason?: string | null;
  locked_at?: string | null;
  owner_user_id?: string | null;
  max_projects: number;
  max_users: number;
  max_assets: number;
  created_at: string;
  updated_at: string;
  activated_at?: string | null;
  approved_by?: string | null;
  // enriched
  current_projects?: number;
  current_users?: number;
  current_assets?: number;
  plan_name?: string;
  subscription_end_date?: string | null;
}

export interface SubscriptionPlan {
  id: string;
  code: SubscriptionPlanCode;
  name: string;
  description?: string | null;
  price_monthly: number;
  price_yearly: number;
  currency: string;
  max_projects: number;
  max_users: number;
  max_assets: number;
  features: string[];
  is_active: boolean;
  display_order: number;
}

export interface CompanySubscription {
  id: string;
  company_id: string;
  plan_code: SubscriptionPlanCode;
  status_code: SubscriptionStatus;
  start_date: string;
  end_date?: string | null;
  billing_cycle: BillingCycle;
  amount_paid: number;
  payment_method?: string | null;
  notes?: string | null;
  created_at: string;
}

export interface FeatureFlag {
  id: string;
  plan_code: SubscriptionPlanCode;
  feature_key: string;
  is_enabled: boolean;
  config: Record<string, unknown>;
}

export interface UsageTracking {
  id: string;
  company_id: string;
  metric_key: string;
  metric_value: number;
  period_date: string;
}

export interface UsageSummary {
  projects_count: number;
  users_count: number;
  assets_count: number;
  work_orders_count: number;
  logs_count: number;
  storage_mb: number;
}

// Feature key → label mapping
export const FEATURE_LABELS: Record<string, { label: string; icon: string; tier: SubscriptionPlanCode }> = {
  projects_basic: { label: 'Dự án cơ bản', icon: 'ri-building-2-line', tier: 'free' },
  projects_full: { label: 'Dự án đầy đủ', icon: 'ri-building-4-line', tier: 'pro' },
  boq: { label: 'BOQ & Dự toán', icon: 'ri-file-list-3-line', tier: 'basic' },
  progress: { label: 'Tiến độ thi công', icon: 'ri-bar-chart-grouped-line', tier: 'basic' },
  inventory: { label: 'Kho vật tư', icon: 'ri-store-3-line', tier: 'basic' },
  payments: { label: 'Thanh toán', icon: 'ri-money-dollar-circle-line', tier: 'basic' },
  quality: { label: 'Chất lượng QA/QC', icon: 'ri-shield-check-line', tier: 'basic' },
  approvals: { label: 'Phê duyệt', icon: 'ri-checkbox-circle-line', tier: 'basic' },
  maintenance_basic: { label: 'Bảo dưỡng cơ bản', icon: 'ri-plant-line', tier: 'free' },
  maintenance_full: { label: 'Bảo dưỡng đầy đủ', icon: 'ri-leaf-line', tier: 'pro' },
  predictive: { label: 'Dự báo rủi ro AI', icon: 'ri-brain-line', tier: 'pro' },
  cost_tracking: { label: 'Theo dõi chi phí BĐ', icon: 'ri-coin-line', tier: 'pro' },
  sla: { label: 'SLA & Hiệu suất', icon: 'ri-timer-line', tier: 'pro' },
  lifecycle: { label: 'Vòng đời tài sản', icon: 'ri-loop-left-line', tier: 'pro' },
  kpi_dashboard: { label: 'KPI Dashboard', icon: 'ri-dashboard-line', tier: 'pro' },
  advanced_dashboard: { label: 'Dashboard nâng cao', icon: 'ri-line-chart-line', tier: 'pro' },
  bulk_ops: { label: 'Thao tác hàng loạt', icon: 'ri-list-check-3', tier: 'basic' },
  routes: { label: 'Tuyến bảo dưỡng', icon: 'ri-route-line', tier: 'pro' },
  contracts: { label: 'Hợp đồng BĐ', icon: 'ri-file-paper-2-line', tier: 'pro' },
  mobile_view: { label: 'Giao diện mobile', icon: 'ri-smartphone-line', tier: 'free' },
  export_pdf: { label: 'Xuất báo cáo PDF', icon: 'ri-file-pdf-line', tier: 'basic' },
  export_all: { label: 'Xuất tất cả định dạng', icon: 'ri-download-2-line', tier: 'pro' },
  api_access: { label: 'API Access', icon: 'ri-code-box-line', tier: 'pro' },
  white_label: { label: 'White Label', icon: 'ri-palette-line', tier: 'enterprise' },
  custom_domain: { label: 'Custom Domain', icon: 'ri-global-line', tier: 'enterprise' },
  priority_support: { label: 'Hỗ trợ ưu tiên 24/7', icon: 'ri-customer-service-2-line', tier: 'enterprise' },
  audit_advanced: { label: 'Audit log nâng cao', icon: 'ri-history-line', tier: 'enterprise' },
  compliance: { label: 'Audit & Compliance', icon: 'ri-shield-keyhole-line', tier: 'enterprise' },
};

export const PLAN_CONFIG: Record<SubscriptionPlanCode, { label: string; color: string; bg: string; border: string; text: string; badge: string }> = {
  free: { label: 'Free', color: 'slate', bg: 'bg-slate-100', border: 'border-slate-200', text: 'text-slate-600', badge: 'bg-slate-100 text-slate-600 border-slate-200' },
  basic: { label: 'Basic', color: 'blue', bg: 'bg-sky-50', border: 'border-sky-200', text: 'text-sky-700', badge: 'bg-sky-100 text-sky-700 border-sky-200' },
  pro: { label: 'Pro', color: 'green', bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700', badge: 'bg-green-100 text-green-700 border-green-200' },
  enterprise: { label: 'Enterprise', color: 'violet', bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', badge: 'bg-amber-100 text-amber-700 border-amber-200' },
};
